// src/PCViewer/PointCloudLoadingPanel.tsx
//
// Top-left status box (mirrors the Measurement Review panel) showing how loaded
// the CURRENT VIEW is. Potree streams the octree by LOD, so "% of the whole
// cloud" is meaningless (you never load a useful fraction of a multi-GB octree);
// instead we show the fraction of the nodes this view needs that are already
// loaded — it reaches 100% when nothing is in flight (Potree.numNodesLoading===0),
// i.e. the view is as detailed as it will get and is safe to measure.
import React from 'react';
import { Tooltip } from 'antd';

interface Props {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    viewer: any;
}

const fmtPoints = (n: number): string => {
    if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(0)}k`;
    return `${n}`;
};

export const PointCloudLoadingPanel: React.FC<Props> = ({ viewer }) => {
    const [state, setState] = React.useState<{ has: boolean; percent: number; points: number }>({
        has: false,
        percent: 100,
        points: 0,
    });
    // Throttle re-renders: only update when the shown values change (and not more
    // than ~8×/sec), since the viewer fires 'update' every frame.
    const lastRef = React.useRef({ percent: -1, label: '', t: 0, has: false });
    // Adaptive denominator: the rendered-point count of the last settled view. A
    // nearby view has a similar count, so `points / target` tracks the points as
    // they appear and lands near 100% — far better than a fixed budget.
    const targetRef = React.useRef(0);

    React.useEffect(() => {
        if (!viewer?.addEventListener) return;
        const onUpdate = () => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const Potree = (window as any).Potree;
            const pcs = viewer.scene?.pointclouds ?? [];
            const has = pcs.length > 0;
            if (!has) {
                if (lastRef.current.has) {
                    lastRef.current = { percent: -1, label: '', t: 0, has: false };
                    setState({ has: false, percent: 100, points: 0 });
                }
                return;
            }

            let points = 0;
            let nodes = 0;
            for (const pc of pcs) {
                points += pc.numVisiblePoints ?? 0;
                nodes += pc.visibleNodes?.length ?? 0;
            }
            const loading = Potree?.numNodesLoading ?? 0;
            let percent: number;
            if (loading === 0) {
                // Nothing in flight → the view is as detailed as it will get.
                percent = 100;
                if (points > 0) targetRef.current = points; // remember this view's size
            } else if (targetRef.current > 0) {
                // Denominator = the last settled view's point count (nearby views are
                // similar), so the % tracks the points as they appear. Cap at 99% —
                // 100% only when actually idle.
                percent = Math.max(
                    0,
                    Math.min(99, Math.round((points / targetRef.current) * 100))
                );
            } else {
                // Very first load — no reference yet. A fresh load fills visibleNodes
                // from zero, so loaded / (loaded + in-flight) climbs 0 → ~100 with it.
                percent = Math.max(0, Math.min(99, Math.round((nodes / (nodes + loading)) * 100)));
            }
            const label = fmtPoints(points);

            const now =
                typeof performance !== 'undefined' && performance.now ? performance.now() : 0;
            const changed = percent !== lastRef.current.percent || label !== lastRef.current.label;
            // Always let 100% through promptly so "done" isn't held back by the throttle.
            if (changed && (percent === 100 || now - lastRef.current.t > 120)) {
                lastRef.current = { percent, label, t: now, has: true };
                setState({ has: true, percent, points });
            }
        };

        viewer.addEventListener('update', onUpdate);
        return () => viewer.removeEventListener('update', onUpdate);
    }, [viewer]);

    if (!state.has) return null;
    const loading = state.percent < 100;
    // Dot colour by load level: 0–75 red, 76–99 yellow, 100 green.
    const dotLevel =
        state.percent >= 100 ? 'is-ready' : state.percent >= 76 ? 'is-mid' : 'is-low';

    return (
        <Tooltip
            title={
                <div className="neighbor-validation-tooltip__body">
                    <strong>Point cloud rendering progress</strong>
                    <div>How much of the current view has streamed in and rendered.</div>
                    <div>Reaches 100% when nothing more is loading.</div>
                </div>
            }
            placement="right"
            overlayClassName="neighbor-validation-tooltip"
        >
            <div className="pointcloud-loading-panel">
                <span className={`pointcloud-loading-panel__dot ${dotLevel}`} aria-hidden="true" />
                <span className="pointcloud-loading-panel__pct">{state.percent}%</span>
                <span className="pointcloud-loading-panel__points">{fmtPoints(state.points)} pts rendered</span>
            </div>
        </Tooltip>
    );
};
